/**
 * Shared expandable datagrid for the Price Rules and Condition Discounts tabs
 * (docs/expandable-index-pages.md). The 13-column legacy table collapses to a
 * scannable summary row; the full criteria live in the expanded panel.
 * Priority ordering is the core mental model, so each row leads with its
 * position and keeps the move up/down controls.
 */
import {
  BulkDeleteButton,
  Datagrid,
  FunctionField,
  useGetList,
  useGetOne,
  useListContext,
  useNotify,
} from 'react-admin';
import { Avatar, Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { getCurrencySymbol } from '@src/utils/currency';
import { requirePartner } from '@src/providers/dataProvider/shared';
import type { PartnerSettings } from '@src/settings';
import type { ConditionOption } from '@src/conditions';

import { updatePricingRulePosition } from '../pricingRules/updatePricingRulePosition';
import { facetPairs, formatConditionDiscount, formatRuleDiscount, ruleScopeSummary, sourceColumnLabel } from './ruleFormat';
import RuleExpand from './RuleExpand';
import type { PricingRule, PricingType } from '../pricingRules/types';

/** Secondary-styled "All" for criteria a rule doesn't constrain. */
const AllValue = () => (
  <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>
    All
  </Typography>
);

const PositionControls = ({ rule }: { rule: PricingRule }) => {
  const notify = useNotify();
  // refetch from list context instead of global refresh() to avoid refetching
  // unrelated data (settings, facets, …) on every position change.
  const { data, refetch } = useListContext<PricingRule>();

  const move = async (event: React.MouseEvent, newPosition: number) => {
    event.stopPropagation();
    if (newPosition < 0) return;
    try {
      const partner = requirePartner();
      await updatePricingRulePosition({ partnerId: partner.id, ruleId: rule.id, newPosition });
      void refetch();
    } catch (error) {
      console.error('RulesDatagrid: move failed', {
        ruleId: rule.id,
        error: error instanceof Error ? error.message : error,
      });
      notify(error instanceof Error ? error.message : 'Failed to move rule', { type: 'error' });
    }
  };

  const isFirst = rule.position === 0;
  const lastRule = data?.length ? data[data.length - 1] : undefined;
  const isLast = lastRule != null && rule.position === lastRule.position;

  return (
    <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
      <Tooltip title={isFirst ? 'Already the highest priority' : 'Move up (higher priority)'}>
        <span>
          <IconButton size="small" onClick={event => move(event, rule.position - 1)} disabled={isFirst}>
            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={isLast ? 'Already the lowest priority' : 'Move down (lower priority)'}>
        <span>
          <IconButton size="small" onClick={event => move(event, rule.position + 1)} disabled={isLast}>
            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

interface RulesDatagridProps {
  pricingType: PricingType;
}

const RulesDatagrid = ({ pricingType }: RulesDatagridProps) => {
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencySymbol = getCurrencySymbol(settings?.primaryCurrencyCode);
  const isCondition = pricingType === 'condition';
  const { data: conditions = [] } = useGetList<ConditionOption>('conditions', {}, { enabled: isCondition });
  const conditionLabels = Object.fromEntries(conditions.map(({ code, label }) => [code, label]));

  return (
    <Datagrid
      bulkActionButtons={<BulkDeleteButton />}
      expand={<RuleExpand pricingType={pricingType} />}
      rowClick="expand"
      sx={{ '& .RaDatagrid-rowCell': { verticalAlign: 'middle' } }}
    >
      <FunctionField<PricingRule>
        source="position"
        label="Rule"
        sortable={false}
        render={rule => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'grey.100',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {rule.position + 1}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {ruleScopeSummary(rule, pricingType, conditionLabels)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {`#${rule.id}${facetPairs(rule).length ? ` · ${facetPairs(rule).length} facet match${facetPairs(rule).length === 1 ? '' : 'es'}` : ''}`}
              </Typography>
            </Box>
          </Box>
        )}
      />
      {isCondition ? (
        <FunctionField<PricingRule>
          label="Condition"
          sortable={false}
          render={rule => (rule.condition ?
            <Chip size="small" variant="outlined" label={conditionLabels[rule.condition] ?? rule.condition} /> :
            <AllValue />)}
        />
      ) : (
        <FunctionField<PricingRule>
          label="Source price"
          sortable={false}
          render={rule => sourceColumnLabel(rule.sourceColumn)}
        />
      )}
      <FunctionField<PricingRule>
        label="Department"
        sortable={false}
        render={rule => rule.department ?? <AllValue />}
      />
      <FunctionField<PricingRule>
        label="Category"
        sortable={false}
        render={rule => rule.category ?? <AllValue />}
      />
      <FunctionField<PricingRule>
        label={isCondition ? 'Discount' : 'Adjustment'}
        sortable={false}
        textAlign="right"
        render={rule => (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {isCondition ?
              formatConditionDiscount(rule) :
              formatRuleDiscount(rule, currencySymbol)}
          </Typography>
        )}
      />
      <FunctionField<PricingRule>
        label="Priority"
        sortable={false}
        textAlign="right"
        render={rule => <PositionControls rule={rule} />}
      />
    </Datagrid>
  );
};

export default RulesDatagrid;
